import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dequal } from 'dequal';
import { tinctApi, useGetChatAgentMessages } from '@tinct/core-api';
import { createAiChatEventBus } from '@tinct/web-ai';
import type { ChatMessage } from '@tinct/core-api-models';
import type { AiChatEventBus } from '@tinct/web-ai';
import {useNavigate} from "react-router-dom";
import {useCurrentWorkspaceUrlPrefix} from "@tinct/core-workspace";
import { createChatStreamHandlers } from '../../../utils/chat-stream-handlers';
import { NEW_CAMPAIGN_DIALOG_HASH } from '../../../modals/new-campaign-dialog/new-campaign-dialog';

const PAGE_SIZE = 20;

export interface UseCampaignChatOptions {
  campaignId: string | undefined;
  pendingMessage: string | null;
}

export interface UseCampaignChatReturn {
  messages: ChatMessage[] | undefined;
  eventBus: AiChatEventBus;
  isLoadingMore: boolean;
  hasMore: boolean;
  handleSendMessage: (message: string) => void;
  handleLoadMore: () => void;
}

export function useCampaignChat({
  campaignId,
  pendingMessage
}: UseCampaignChatOptions): UseCampaignChatReturn {
  const navigate = useNavigate();
  const workspaceUrl = useCurrentWorkspaceUrlPrefix();
  const hasStartedRef = useRef(false);
  const eventBus = useMemo(() => createAiChatEventBus(), []);

  // ==========================================================================
  // Message state
  // ==========================================================================

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [nextOffset, setNextOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // ==========================================================================
  // Fetch initial messages (newest first — backend returns created DESC)
  // ==========================================================================

  const { data: initialData, isLoading: isMessagesLoading } = useGetChatAgentMessages(
    {
      staticParams: { campaignId: campaignId ?? '' },
      offset: 0,
      limit: PAGE_SIZE,
    },
    {
      enabled: !!campaignId && !isInitialized,
      refetchOnMount: 'always',
    }
  );

  // Derive state during render (React-approved pattern).
  // See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevData, setPrevData] = useState<ChatMessage[]>();
  if (initialData && !dequal(initialData.data, prevData)) {
    setPrevData(initialData.data);

    const total = initialData.pagination.count;
    if (total > 0) {
      setMessages([...initialData.data].reverse());
      setTotalCount(total);
      setNextOffset(PAGE_SIZE);
      setHasMore(total > PAGE_SIZE);
    }
    setIsInitialized(true);
  }

  // Side effect: start a new chat when there are no existing messages
  useEffect(() => {
    if (!isInitialized || messages.length > 0 || !campaignId) return;
    if (hasStartedRef.current) return;
    if (isMessagesLoading) return;
    hasStartedRef.current = true;
    if (pendingMessage) return;

    navigate(
      { pathname: `${workspaceUrl}/campaigns`, hash: NEW_CAMPAIGN_DIALOG_HASH },
      { replace: true }
    );

  }, [isInitialized, messages.length, campaignId, eventBus, navigate, workspaceUrl, pendingMessage, isMessagesLoading]);

  // ==========================================================================
  // Load more (older messages — next page in DESC order)
  // ==========================================================================

  const handleLoadMore = useCallback(() => {
    if (!campaignId || isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    tinctApi.campaigns.getCampaignChatAgentMessages({
      staticParams: { campaignId },
      offset: nextOffset,
      limit: PAGE_SIZE,
    }).then((response) => {
      setMessages((prev) => [...[...response.data].reverse(), ...prev]);
      const newOffset = nextOffset + response.data.length;
      setNextOffset(newOffset);
      setHasMore(newOffset < totalCount);
      setIsLoadingMore(false);
    }).catch(() => {
      setIsLoadingMore(false);
    });
  }, [campaignId, isLoadingMore, hasMore, nextOffset, totalCount]);

  // ==========================================================================
  // Send message
  // ==========================================================================

  const handleSendMessage = useCallback((message: string) => {
    if (!campaignId) return;
    const { onEvent, onError, onComplete } = createChatStreamHandlers(eventBus);
    tinctApi.campaigns.postCampaignMessageChat(
      campaignId,
      message,
      onEvent,
      onError,
      onComplete
    );
  }, [campaignId, eventBus]);

  return {
    messages: isInitialized ? messages : undefined,
    eventBus,
    isLoadingMore,
    hasMore,
    handleSendMessage,
    handleLoadMore,
  };
}
